import React, { FC, useState } from "react";
import { useIntl } from "react-intl";
import AppointmentPaymentAddDialogContainer from "../AppointmentPaymentAddDialogContainer";
import getFormattedPrice from "../../../common/getFormattedPrice";
import { PaymentOptionsData } from "../AppointmentPaymentAddForm";
import { AppointmentPayment } from "../AppointmentPaymentGrid/types";
import AppointmentPaymentListCard from "../AppointmentPaymentListCard";
import { Appointment } from "./types";
import useAppointmentPaymentAddDialogContainer from "./useAppointmentPaymentAddDialogContainer";
import AppointmentAdjustPriceDialogContainer from "../AppointmentAdjustPriceDialogContainer";
import useAdjustPriceDialogContainer from "./useAdjustPriceDialogContainer";
import AppointmentPaymentRefundDialogContainer from "../AppointmentPaymentRefundDialogContainer";
import useAddRefundDialogContainer from "./useAddRefundDialogContainer";
import { AppointmentRefund } from "../AppointmentRefundGrid/types";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import { useUIContext } from "../../Unknown/UIContext";

export type AppointmentPaymentListCardContainerProps = {
  payments: AppointmentPayment[];
  refunds: AppointmentRefund[];
  appointment: Appointment | null;
  paymentOptionsData: PaymentOptionsData;
  currencyCode: string;
  centerLocale: string;
  isRefundDue: boolean;
  isReceiptReady: boolean;
  couponCode?: string;
  handleAddPayment: () => void;
};

const AppointmentPaymentListCardContainer: FC<
  AppointmentPaymentListCardContainerProps
> = ({
  payments,
  refunds,
  couponCode,
  appointment,
  currencyCode,
  centerLocale,
  isRefundDue,
  isReceiptReady,
  paymentOptionsData,
  handleAddPayment,
}) => {
  const [isReceiptDownloading, setIsReceiptDownloading] = useState(false);
  const [isReceiptSending, setIsReceiptSending] = useState(false);

  const resendAppointmentReceipt = useFirebaseAppFunction(
    "resendAppointmentReceipt",
  );

  const generateReceiptSignedUrl = useFirebaseAppFunction(
    "generateReceiptSignedUrl",
  );

  const { setAlert } = useUIContext();

  const { handleOpen: handleAddPaymentDialogOpen, props: addPaymentProps } =
    useAppointmentPaymentAddDialogContainer({
      appointment,
      currencyCode,
      centerLocale,
      paymentOptionsData,
      isRefundDue,
      handleAddPayment,
    });

  const handleViewReceiptClick = async () => {
    try {
      setIsReceiptDownloading(true);
      if (!appointment?.id) return;

      const { data } = await generateReceiptSignedUrl({
        appointmentId: appointment?.id,
      });

      window.open(data.downloadUrl, "_blank");
    } catch (error) {
      setAlert({
        message: "Error generating receipt",
        severity: "error",
        isShown: true,
      });
    } finally {
      setIsReceiptDownloading(false);
    }
  };

  const handleSendReceiptClick = async () => {
    if (!appointment?.id) return;
    setIsReceiptSending(true);

    try {
      await resendAppointmentReceipt({
        appointmentId: appointment?.id,
      });

      setAlert({
        message: "Receipt sent successfully",
        severity: "success",
        isShown: true,
      });
    } catch (error) {
      setAlert({
        message: "Error sending receipt",
        severity: "error",
        isShown: true,
      });
    } finally {
      setIsReceiptSending(false);
    }
  };

  const { handleOpen: handleAdjustPriceDialogOpen, props: adjustPriceProps } =
    useAdjustPriceDialogContainer({
      appointment,
      currencyCode,
      centerLocale,
      handleSuccess: handleAddPayment,
    });

  const { props: addRefundProps, handleOpen: handleOpenRefundDialog } =
    useAddRefundDialogContainer({
      refunds,
      currencyCode,
      appointment,
      isRefundDue,
      handleSuccess: handleAddPayment,
    });

  const intl = useIntl();

  const isPaymentButtonShown = !!appointment?.remainingBalance && !isRefundDue;

  const totalPrice = getFormattedPrice({
    value: appointment?.totalPrice || 0,
    currencyCode,
    intl,
  });

  const remainingBalance = getFormattedPrice({
    value: appointment?.remainingBalance || 0,
    currencyCode,
    intl,
  });

  return (
    <>
      <AppointmentPaymentListCard
        payments={payments}
        refunds={refunds}
        totalPrice={totalPrice}
        remainingBalance={remainingBalance}
        isRefundDue={isRefundDue}
        isReceiptReady={isReceiptReady}
        isPaymentButtonShown={isPaymentButtonShown}
        isReceiptDownloading={isReceiptDownloading}
        isReceiptSending={isReceiptSending}
        couponCode={couponCode}
        handleAddClick={handleAddPaymentDialogOpen}
        handleAdjustPriceClick={handleAdjustPriceDialogOpen}
        handleOpenRefundDialog={handleOpenRefundDialog}
        handleViewReceiptClick={handleViewReceiptClick}
        handleSendReceiptClick={handleSendReceiptClick}
      />
      <AppointmentPaymentAddDialogContainer {...addPaymentProps} />
      {addRefundProps && (
        <AppointmentPaymentRefundDialogContainer {...addRefundProps} />
      )}
      <AppointmentAdjustPriceDialogContainer {...adjustPriceProps} />
    </>
  );
};

export default AppointmentPaymentListCardContainer;
